"use client";

import { cn } from "@/lib/utils/general-utils";
import {
  CircleAlert as ExclamationCircleIcon,
  CircleCheck as CheckCircleIcon,
  ChevronsUpDown as ChevronUpDownIcon,
  TriangleAlert as ExclamationTriangleIcon,
} from "lucide-react";
import React, { forwardRef } from "react";

export type SelectVariant = "form" | "filter" | "navigation" | "inline";
export type SelectSize = "sm" | "md" | "lg";
export type ValidationState = "default" | "error" | "success" | "warning";

export interface SelectProps extends Omit<
  React.ComponentPropsWithoutRef<"select">,
  "className" | "children" | "size"
> {
  children: React.ReactNode;
  className?: string;
  multiple?: boolean;
  variant?: SelectVariant;
  size?: SelectSize;
  state?: ValidationState;
  error?: string;
  success?: string;
  warning?: string;
  showValidationMessage?: boolean;
  loading?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  animate?: boolean;
  placeholder?: string;
  showPlaceholder?: boolean;
}

function getValidationIcon(state: ValidationState) {
  switch (state) {
    case "error":
      return ExclamationCircleIcon;
    case "success":
      return CheckCircleIcon;
    case "warning":
      return ExclamationTriangleIcon;
    default:
      return null;
  }
}

function getValidationMessage(
  error?: string,
  success?: string,
  warning?: string,
): { message: string; state: ValidationState } | null {
  if (error) return { message: error, state: "error" };
  if (success) return { message: success, state: "success" };
  if (warning) return { message: warning, state: "warning" };
  return null;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    {
      children,
      className,
      multiple,
      variant: _variant = "form",
      size = "md",
      state = "default",
      error,
      success,
      warning,
      showValidationMessage = true,
      loading = false,
      icon,
      animate = false,
      placeholder,
      showPlaceholder = true,
      disabled,
      ...props
    },
    ref,
  ) {
    const validationInfo = getValidationMessage(error, success, warning);
    const finalState = validationInfo ? validationInfo.state : state;
    const ValidationIcon = getValidationIcon(finalState);

    const sizeClasses = {
      sm: "py-1 pl-2.5 text-sm",
      md: "py-1.5 pl-3 text-sm",
      lg: "py-2 pl-3.5 text-base",
    }[size];

    const iconSize = {
      sm: "h-4 w-4",
      md: "h-4 w-4",
      lg: "h-5 w-5",
    }[size];

    return (
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            {React.createElement(icon, {
              className: cn(iconSize, "text-gray-400"),
            })}
          </div>
        )}

        <select
          ref={ref}
          multiple={multiple}
          disabled={disabled || loading}
          className={cn(
            "relative block w-full appearance-none rounded-lg border bg-white text-left",
            sizeClasses,
            multiple ? "pr-3" : "pr-9",
            "border-gray-300 hover:border-gray-400 transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            finalState === "error" && "border-red-500 hover:border-red-500",
            finalState === "success" &&
              "border-green-500 hover:border-green-500",
            finalState === "warning" &&
              "border-amber-500 hover:border-amber-500",
            icon && "pl-10",
            loading && "opacity-50 cursor-wait",
            className,
          )}
          {...props}
        >
          {placeholder && showPlaceholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
        </select>

        {/* Chevron — hidden when multiple */}
        {!multiple && (
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
          </span>
        )}

        {loading && (
          <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <div
              className={cn(
                "animate-spin rounded-full border-2 border-gray-300 border-t-primary",
                {
                  "h-3 w-3": size === "sm",
                  "h-4 w-4": size === "md",
                  "h-5 w-5": size === "lg",
                },
              )}
            />
          </div>
        )}

        {ValidationIcon && !loading && (
          <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <ValidationIcon
              className={cn(iconSize, {
                "text-red-500": finalState === "error",
                "text-green-500": finalState === "success",
                "text-amber-500": finalState === "warning",
              })}
            />
          </div>
        )}

        {showValidationMessage && validationInfo && (
          <div
            className={cn("mt-1 text-sm flex items-center gap-1", {
              "text-status-error-text": validationInfo.state === "error",
              "text-status-success-text": validationInfo.state === "success",
              "text-status-warning-text": validationInfo.state === "warning",
            })}
          >
            {ValidationIcon && (
              <ValidationIcon className="h-4 w-4 flex-shrink-0" />
            )}
            <span>{validationInfo.message}</span>
          </div>
        )}
      </div>
    );
  },
);
